import React, { useEffect, useState } from "react";
import HeaderCustom from "../../../components/HeaderCustom";
import { Alert, Snackbar } from "@mui/material";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useSelector } from "react-redux";
import { getHome } from "../../../api/ApiService";
import DashboardActividad from "./components/DashboardActividad";
import DashboardCharts from "./components/DashboardCharts";
import DashboardStatus from "./components/DashboardStatus";

const getDefaultDateRange = () => {
    const now = new Date();
    // const start = new Date(now.getFullYear(), now.getMonth(), 1);
    //tomar desde el enero 2025
    const start = new Date(2026, 0, 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    const formatDate = (value) => {
        const year = value.getFullYear();
        const month = String(value.getMonth() + 1).padStart(2, "0");
        const day = String(value.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    return {
        desde: formatDate(start),
        hasta: formatDate(end),
    };
};

const normalizeHomeResponse = (response) => {
    if (!response) return null;
    if (response?.kpis) return response;
    if (response?.data?.kpis) return response.data;
    return null;
};

function HomeAdminPage() {
    const defaultRange = getDefaultDateRange();

    const [cargando, setCargando] = useState(false);
    const [open, setOpen] = useState(false);
    const [severity, setSeverity] = useState("");
    const [mensaje, setMensaje] = useState("");
    const [dateError, setDateError] = useState("");

    const [dataHome, setDataHome] = useState(null);
    const [fechaDesde, setFechaDesde] = useState(defaultRange.desde);
    const [fechaHasta, setFechaHasta] = useState(defaultRange.hasta);

    const stateToken = useSelector((state) => state.reducerGlobal.token);

    const fechaDesdeDayjs = dayjs(fechaDesde);
    const fechaHastaDayjs = dayjs(fechaHasta);

    const handleFechaDesdeChange = (newValue) => {
        if (!newValue || !newValue.isValid()) return;

        const formattedDate = newValue.format("YYYY-MM-DD");
        setFechaDesde(formattedDate);

        if (dayjs(fechaHasta).isBefore(newValue, "day")) {
            setFechaHasta(formattedDate);
        }

        setDateError("");
    };

    const handleFechaHastaChange = (newValue) => {
        if (!newValue || !newValue.isValid()) return;

        if (newValue.isBefore(dayjs(fechaDesde), "day")) {
            setDateError("La fecha hasta no puede ser menor que la fecha desde.");
            return;
        }

        setFechaHasta(newValue.format("YYYY-MM-DD"));
        setDateError("");
    };

    const handleHome = () => {
        if (dayjs(fechaHasta).isBefore(dayjs(fechaDesde), "day")) {
            setMensaje("Rango invalido: la fecha hasta no puede ser menor que desde.");
            setSeverity("error");
            setOpen(true);
            return;
        }

        setCargando(false);

        const body = {
            desde: fechaDesde,
            hasta: fechaHasta,
        };

        getHome(stateToken?.access_token, body)
            .then((response) => {
                setDataHome(normalizeHomeResponse(response));
                setCargando(true);
            })
            .catch((error) => {
                console.log("error-getHome", error);
                setCargando(true);
                setDataHome(null);

                setMensaje("Error al cargar los datos");
                setSeverity("error");
                setOpen(true);
            });
    };

    useEffect(() => {
        if (stateToken) {
            handleHome();
        }
    }, [stateToken, fechaDesde, fechaHasta]);

    const handleClose = () => {
        setOpen(false);
    };

    return (
        <HeaderCustom>
            <div className="min-h-screen bg-gray-100 p-4 sm:p-6">
                <div className="mb-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                    <div className="mb-3">
                        <h2 className="text-base font-semibold text-gray-900">Filtros de fecha</h2>
                        <p className="text-sm text-gray-500">Selecciona el rango para el dashboard.</p>
                    </div>

                    <LocalizationProvider adapterLocale="es" dateAdapter={AdapterDayjs}>
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                            <DatePicker
                                label="Desde"
                                value={fechaDesdeDayjs}
                                format="DD/MM/YYYY"
                                maxDate={fechaHastaDayjs}
                                onChange={handleFechaDesdeChange}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        size: "small",
                                    },
                                }}
                            />

                            <DatePicker
                                label="Hasta"
                                value={fechaHastaDayjs}
                                format="DD/MM/YYYY"
                                minDate={fechaDesdeDayjs}
                                onChange={handleFechaHastaChange}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        size: "small",
                                        error: Boolean(dateError),
                                        helperText: dateError || " ",
                                    },
                                }}
                            />
                        </div>
                    </LocalizationProvider>
                </div>

                {cargando ? (
                    <div className="bg-white rounded-xl shadow-xl overflow-hidden p-6">
                        <DashboardActividad kpis={dataHome?.kpis} />

                        <DashboardCharts
                            series={dataHome?.series}
                            kpis={dataHome?.kpis}
                        />

                        <DashboardStatus
                            topLocales={dataHome?.top_locales || []}
                            topCategorias={dataHome?.top_categorias || []}
                        />
                    </div>
                ) : (
                    <div className="bg-white rounded-xl shadow-md p-8">
                        <div className="h-6 bg-gray-200 rounded w-1/3 mb-6 animate-pulse" />
                        <div className="space-y-4">
                            <div className="h-10 bg-gray-200 rounded animate-pulse" />
                            <div className="h-10 bg-gray-200 rounded animate-pulse" />
                            <div className="h-24 bg-gray-200 rounded animate-pulse" />
                        </div>
                    </div>
                )}
            </div>

            <Snackbar open={open} autoHideDuration={2000} onClose={handleClose}>
                <Alert onClose={handleClose} severity={severity} elevation={6} variant="filled">
                    {mensaje}
                </Alert>
            </Snackbar>
        </HeaderCustom>
    );
}

export default HomeAdminPage;
